/**
 * Quick-add's state, validation and submit — the logic BOTH variants share.
 *
 * WHY THIS HOOK EXISTS. The doctrine says a `.mobile.jsx` is a *reduction* of
 * the web variant and never a fork (CLAUDE.md §12). That is easy to say and
 * easy to violate: the natural way to write a mobile screen is to copy the
 * desktop one and delete things, and three months later the two build slightly
 * different payloads and only one of them sets the interaction type.
 *
 * So the payload is built HERE, once. Both variants render different markup
 * over the same hook, and `tests/mobile.test.jsx` asserts that a narrow
 * viewport submits byte-for-byte what the desktop dialog submits. The variants
 * differ in layout and in nothing else.
 */
import { useMutation, useQuery } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { fromPersianDigits } from './format.js';
import {
  ADMIN_LOOKUPS_QUERY,
  CREATE_CONTACT,
  RECORD_INTERACTION,
  SET_INTERESTS,
} from './queries.js';
import {
  EMPTY_CRM_FIELDS,
  centerNameError,
  crmFieldsInput,
} from '../components/contact/ContactCrmFields.web.jsx';
import { S } from './strings.js';

export const EMPTY_QUICK_ADD = {
  firstName: '',
  lastName: '',
  phone: '',
  channels: [],
  fieldOfWorkId: '',
  note: '',
  // The product this contact is interested in. Applied with `setInterests`
  // after the create, because interests are their own relation rather than a
  // column — the same mutation the profile's interests panel uses, so there is
  // one way a product interest gets recorded.
  productId: '',
  // The CRM profile fields, behind a disclosure in both variants. One object,
  // one payload builder, shared with the edit form.
  ...EMPTY_CRM_FIELDS,
};

export function useQuickAdd({ open, onCreated, onDone }) {
  const [form, setForm] = useState(EMPTY_QUICK_ADD);
  const [error, setError] = useState(null);
  const [justAdded, setJustAdded] = useState(null);
  const lastNameRef = useRef(null);

  const { data: channelData } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'COMMUNICATION_CHANNEL', includeInactive: false },
    skip: !open,
  });
  // FIELD_OF_WORK and PRODUCT are deliberately NOT fetched here: both are
  // rendered by `LookupField`, which owns its own query so it can also CREATE
  // a missing option. Fetching them here as well would be the same request
  // twice per open — and, in a test, two consumers racing one mock.
  const { data: typeData } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'INTERACTION_TYPE', includeInactive: false },
    skip: !open,
  });
  // Needed by `submit` to apply the center-name rule before the round trip, so
  // it is read here rather than only inside ContactCrmFields.
  const { data: facilityTypeData } = useQuery(ADMIN_LOOKUPS_QUERY, {
    variables: { kind: 'FACILITY_TYPE', includeInactive: false },
    skip: !open,
  });

  const [createContact, { loading: creating }] = useMutation(CREATE_CONTACT);
  const [recordInteraction] = useMutation(RECORD_INTERACTION);
  const [setInterests] = useMutation(SET_INTERESTS);

  const channels = channelData?.lookups ?? [];
  const facilityTypes = facilityTypeData?.lookups ?? [];
  // "The seeded default" for the note's interaction type: the first active
  // INTERACTION_TYPE in the deployment's own sort order. No code names a type —
  // that would be a Parsina value in app code (CLAUDE.md §1).
  const defaultType = (typeData?.lookups ?? [])[0];

  useEffect(() => {
    if (!open) {
      setForm(EMPTY_QUICK_ADD);
      setError(null);
      setJustAdded(null);
    }
  }, [open]);

  const set = useCallback(
    (patch) => setForm((current) => ({ ...current, ...patch })),
    [],
  );

  const toggleChannel = useCallback(
    (code) =>
      setForm((current) => ({
        ...current,
        channels: current.channels.includes(code)
          ? current.channels.filter((c) => c !== code)
          : [...current.channels, code],
      })),
    [],
  );

  /**
   * The contact payload. Exported so a test can assert the two variants agree
   * without going through Apollo — the assertion that matters is about this
   * object, not about which button was pressed.
   */
  const buildInput = useCallback(
    (current = form) => ({
      kind: 'PERSON',
      firstName: current.firstName.trim() || null,
      lastName: current.lastName.trim(),
      fieldOfWorkId: current.fieldOfWorkId || null,
      // Exactly the slice the edit form sends, from exactly the same builder.
      ...crmFieldsInput(current),
      phoneNumbers: fromPersianDigits(current.phone).trim()
        ? [
            {
              // Digits are normalized at the EDGE: `number_raw` preserves the
              // formatting a user typed, not the digit glyphs, and Persian
              // digits stored there make the number unfindable (CLAUDE.md §15).
              numberRaw: fromPersianDigits(current.phone).trim(),
              isPrimary: true,
              channelCodes: current.channels,
            },
          ]
        : [],
    }),
    [form],
  );

  const submit = useCallback(
    async ({ keepOpen }) => {
      if (!form.lastName.trim()) {
        setError(new Error(S.quickAdd.lastNameRequired));
        return null;
      }
      // The server refuses this too; refusing here saves a round trip and
      // names the field. Reads the chosen vocabulary row's own flag — no code
      // in this file knows what a clinic is.
      const centerError = centerNameError(form, facilityTypes);
      if (centerError) {
        setError(new Error(centerError));
        return null;
      }
      setError(null);
      try {
        const { data } = await createContact({ variables: { input: buildInput() } });
        const contact = data.createContact;

        // A product interest is a relation, so it is a second call — the same
        // trade quick-add already makes for the note, and for the same reason:
        // a "create a contact and maybe an interest" special case in the
        // domain would be worse than a few milliseconds on a form that takes
        // twenty seconds to fill.
        if (form.productId) {
          await setInterests({
            variables: { contactId: contact.id, productIds: [form.productId] },
          });
        }

        const note = form.note.trim();
        if (note && defaultType) {
          await recordInteraction({
            variables: {
              input: {
                contactId: contact.id,
                typeId: defaultType.id,
                occurredAt: new Date().toISOString(),
                description: note,
                status: 'DONE',
              },
            },
          });
        }

        onCreated?.(contact);
        if (keepOpen) {
          setForm(EMPTY_QUICK_ADD);
          // The CONTACT, not just its name: the success banner offers to
          // message the person who was just added, and that needs an id.
          setJustAdded(contact);
          lastNameRef.current?.focus();
        } else {
          onDone?.();
        }
        return contact;
      } catch (err) {
        setError(err);
        return null;
      }
    },
    [
      form,
      buildInput,
      createContact,
      recordInteraction,
      setInterests,
      facilityTypes,
      defaultType,
      onCreated,
      onDone,
    ],
  );

  return {
    form,
    set,
    toggleChannel,
    submit,
    buildInput,
    error,
    justAdded,
    creating,
    channels,
    facilityTypes,
    defaultType,
    lastNameRef,
  };
}
